<template>
  <div class="ibox" v-if="$user.isAdminOrCoordinator">
    <div class="ibox-title">
      <h2>Admin Controls</h2>
    </div>
    <div class="ibox-content ibox-content-padding-xs">
      <div class="row">
        <div class="col-sm-4">
          <div class="form-group">
            <label class="control-label">Job Rate</label>
            <div class="input-group">
              <span class="input-group-addon">$</span>
              <p class="form-control-static">{{ job.rate }}</p>
            </div>
          </div>
        </div>
        <div class="col-sm-4">
          <div class="form-group">
            <label class="control-label">Load Rate</label>
            <div class="input-group">
              <span class="input-group-addon">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                v-model="rate"
                class="form-control"
                :disabled="jobIsNotEditable">
            </div>
          </div>
        </div>
        <div class="col-sm-4">
          <div class="form-group">
            <label class="control-label">Commission</label>
            <div class="input-group input-group-text-success" v-bind:class="{ 'input-group-text-danger' : loadCommissionAmount < 0 }">
              <p class="form-control-static">${{ loadCommissionAmountAsMoney }}</p>
              <span class="input-group-addon">{{ loadCommissionPercentage > 0 ? '+' : '' }}{{ loadCommissionPercentage }}%</span>
            </div>
          </div>
        </div>
      </div>

      <h3>
        Additional Charges

        <button
          v-if="jobIsEditable"
          type="button"
          class="btn btn-info btn-xs pull-right"
          data-toggle="collapse"
          data-target=".additional-charge-collapse"
          aria-expanded="false">
          <i class="fa fa-plus"></i> Add Charge
        </button>
      </h3>

      <div class="margin-20"></div>

      <ul class="list-detail list-detail-additional-charges">
        <li v-show="haveNoCharges">
          <div class="text-center text-muted">
            <strong>No additional charges.</strong>
          </div>
        </li>
        <li v-for="charge in loadDecorated.charges">
          <div class="row">
            <div class="col-xs-12 col-sm-4 col-lg-7">
              <p>{{ charge.description }}</p>
            </div>
            <div class="col-xs-3 col-sm-2 col-lg-1">
              <p class="text-right">{{ charge.client_rate }}</p>
            </div>
            <div class="col-xs-3 col-sm-2 col-lg-1">
              <p class="text-right"><span class="text-info">({{ charge.carrier_rate }})</span></p>
            </div>
            <div :class="{ 'col-xs-3 col-sm-2 col-lg-2': jobIsEditable, 'col-xs-6 col-sm-4 col-lg-3': jobIsNotEditable }">
              <p class="text-right">{{ charge.type }}</p>
            </div>
            <div class="col-xs-3 col-sm-2 col-lg-1" v-if="jobIsEditable">
              <ladda-button
                :on-click="handleDeleteCharge"
                :arguments="[charge]"
                class="pull-right btn btn-circle btn-danger">
                <i class="fa fa-trash"></i>
              </ladda-button>
            </div>
          </div>
        </li>
      </ul>

    </div>
    <div class="additional-charge-collapse collapse" v-if="jobIsEditable">
      <div class="ibox-footer">
        <h4>Add Additional Charge</h4>

        <div class="row">
          <div class="col-sm-12 col-md-12 col-lg-5">
            <div class="form-group">
              <label class="control-label">Description</label>
              <input type="text" v-model="charge.description" class="form-control" placeholder="" />
            </div>
          </div>
          <div class="col-sm-4 col-md-4 col-lg-2">
            <div class="form-group">
              <label class="control-label">Client Amount</label>
              <div class="input-group">
                <div class="input-group-addon">$</div>
                <input type="number" v-model="charge.client_rate" class="form-control" placeholder="" />
              </div>
            </div>
          </div>
          <div class="col-sm-4 col-md-4 col-lg-2">
            <div class="form-group">
              <label class="control-label">Carrier Amount</label>
              <div class="input-group">
                <div class="input-group-addon">$</div>
                <input type="number" v-model="charge.carrier_rate" class="form-control" placeholder="" />
              </div>
            </div>
          </div>
          <div class="col-sm-4 col-md-4 col-lg-3">
            <div class="form-group">
              <label class="control-label">Charge Type</label>
              <selectize
                v-model="charge.type"
                name="type"
                :options="chargeTypeOptions">
              </selectize>
            </div>
          </div>
        </div>

        <div class="text-right">
          <ladda-button :on-click="handleAddCharge" type="success" size="small" class="m-t-md m-b-md">Add Charge</ladda-button>
        </div>
      </div>
    </div>
    <div class="ibox-footer">
      <router-link :to="{ name: 'jobs.show', params: { id: load.job.id } }" class="btn btn-info btn-sm pull-left">View Job</router-link>
      <ladda-button v-if="jobIsEditable" :on-click="handleSaveRate" type="success" size="small" class="pull-right">Save</ladda-button>
    </div>
  </div>
</template>

<script>
  import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
  import { SET_LOAD_ATTRIBUTE } from 'vx/mutations'
  import { stateMapper } from 'mixins/loads'
  import { jobHelpers } from 'mixins/jobs'
  import { LaddaButton, Selectize } from 'components/forms'
  import { notify } from 'utils'
  import { sumBy } from 'lodash'
  import accounting from 'accounting'

  export default {
    mixins: [jobHelpers, stateMapper],

    data () {
      return {
        charge: {
          description: null,
          client_rate: null,
          carrier_rate: null,
          type: null
        }
      }
    },

    components: {
      LaddaButton,
      Selectize
    },

    computed: {
      ...mapState('load', ['load']),
      ...mapGetters('load', ['loadDecorated']),

      /**
       * Determine if this load has no additional charges.
       *
       * @return boolean
       */
      haveNoCharges () {
        return this.load.charges.length === 0
      },

      /**
       * Determine if this load has additional charges.
       *
       * @return boolean
       */
      haveCharges () {
        return this.load.charges.length > 0
      },

      /**
       * Load commission amount as money.
       *
       * @return string
       */
      loadCommissionAmountAsMoney () {
        return accounting.formatNumber(this.loadCommissionAmount, 2)
      },

      /**
       * Get the commission amount arrow will earn off of the load.
       *
       * @return float
       */
      loadCommissionAmount () {
        let loadRate = parseFloat(this.load.rate) || 0
        let loadSize = parseFloat(this.load.size) || 0
        let additionalChargesProfit = 0
        let jobProfit
        let loadProfit

        // If we have an approved weighbridge on the load then the load size
        // becomes the total tonnage of the weighbridge.
        if (this.load.weighbridge !== null && this.load.weighbridge.status === 'approved') {
          loadSize = this.load.weighbridge.total_tonnage
        }

        if (this.haveCharges) {
          additionalChargesProfit = sumBy(this.load.charges, charge => {
            if (charge.type === 'single') {
              return charge.client_rate - charge.carrier_rate
            }

            return (charge.client_rate - charge.carrier_rate) * loadSize
          })
        }

        loadProfit = loadSize * loadRate
        jobProfit = loadSize * this.job.rate

        return (jobProfit - loadProfit) + additionalChargesProfit
      },

      /**
       * Get the commission profit as a percentage.
       *
       * @return float
       */
      loadCommissionPercentage () {
        return (((this.load.job.rate - this.load.rate) / this.load.rate) * 100).toFixed(2)
      },

      /**
       * Get the options for the charge type select box
       *
       * @return array
       */
      chargeTypeOptions () {
        return [
          { id: 'single', text: 'Single Charge' },
          { id: 'per_tonne', text: 'Per Tonne' }
        ]
      }
    },

    methods: {
      ...mapMutations('load', [SET_LOAD_ATTRIBUTE]),
      ...mapActions('load', ['updateLoad', 'storeLoadCharge', 'deleteLoadCharge']),

      /**
       * Save rate
       *
       * @return Promise
       */
      handleSaveRate () {
        const notifyUpdated = () => (notify.success('Load rate has been saved.'))

        let payload = {
          id: this.load.id,
          payload: {
            rate: this.load.rate
          }
        }

        return this.updateLoad(payload).then(notifyUpdated)
      },

      /**
       * Store a new charge and clear charge fields
       */
      handleAddCharge () {
        const clearCharge = () => (
          this.charge = {
            description: null,
            client_rate: null,
            carrier_rate: null,
            type: null
          }
        )

        return this.storeLoadCharge({ loadId: this.load.id, payload: this.charge }).then(clearCharge)
      },

      /**
       * Remove load charge from the array of charges
       */
      handleDeleteCharge (charge) {
        return this.deleteLoadCharge(charge.id)
      }
    }
  }
</script>



// WEBPACK FOOTER //
// AdminControls.vue?6f9f1fc7